import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Navbar, Nav, NavDropdown, Container, Dropdown } from "react-bootstrap";
import logo from "../assets/logo.png";
import API_BASE_URL from "../config";
import {
  FaUserCircle,
  FaHome,
  FaQuestionCircle,
  FaCommentDots,
  FaSearch,
  FaPalette,
  FaStar,
  FaHistory,
  FaSlidersH,
} from "react-icons/fa";


const AdminNavbar = () => {
  const navigate = useNavigate();
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [photoObjectUrl, setPhotoObjectUrl] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [subMenuOpen, setSubMenuOpen] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [profile, setProfile] = useState({
    username: "Admin",
    role: "",
    roles: [],        //  ensure always exists
    photo: null,
  });

  const [navbarColor, setNavbarColor] = useState(
    sessionStorage.getItem("navbarColor") || "#080235" // default
  );

  const handleMouseEnter = (dropdown) => setOpenDropdown(dropdown);
  const handleMouseLeave = () => {
    setOpenDropdown(null);
    setSubMenuOpen(null);
  };


  const activeRole =
    sessionStorage.getItem("activeRole") || profile.role;


  useEffect(() => {
    sessionStorage.setItem("activeRole", "ROLE_ADMIN");
  }, []);


  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
      return;
    }

    // ✅ check admin role
    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [navigate]);


  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    let isMounted = true; // avoid state updates after unmount
    let objectUrl = null;

    setPhotoLoading(true);
    setPhotoError(false);

    fetch(`${API_BASE_URL}/api/employees/me/photo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        if (!isMounted) return;
        if (blob && blob.size > 0) {
          objectUrl = URL.createObjectURL(blob);
          setPhotoObjectUrl(objectUrl);
          setProfile((prev) => ({ ...prev, photo: objectUrl }));
        } else {
          // no photo; leave profile.photo as-is or use placeholder
          setPhotoError(true);
        }
      })
      .catch((err) => {
        console.debug("No profile photo or fetch failed:", err);
        if (isMounted) setPhotoError(true);
      })
      .finally(() => {
        if (isMounted) setPhotoLoading(false);
      });

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl); // free memory
      }
      // ensure we also clear previous object url state if any
      setPhotoObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  // useEffect(() => {
  //   const token = sessionStorage.getItem("token");
  //   const employeeId = sessionStorage.getItem("employeeId");

  //   if (!token || !employeeId) return;

  //   fetch(`${API_BASE_URL}/api/users/employee/${employeeId}`, {
  //     headers: { Authorization: `Bearer ${token}` },
  //   })
  //     .then((res) => res.json())
  //     .then((data) => {
  //       if (data) {
  //         setProfile((prev) => ({
  //           ...prev,
  //           username: data.name || prev.username,
  //           roles: data.roles || ["ROLE_MANAGER"],
  //           role: data.roles?.includes("ROLE_MANAGER") ? "MANAGER" : prev.role,
  //         }));
  //       }
  //     })
  //     .catch((err) => console.error("Failed to fetch profile:", err));

  //   // fetch(`${API_BASE_URL}/api/employees/me/photo`, {
  //   //   headers: { Authorization: `Bearer ${token}` },
  //   // })
  //   //   .then((res) => res.blob())
  //   //   .then((blob) => {
  //   //     if (blob.size > 0) {
  //   //       setProfile((prev) => ({
  //   //         ...prev,
  //   //         photo: URL.createObjectURL(blob),
  //   //       }));
  //   //     }
  //   //   })
  //   //   .catch(() => console.log("No profile photo found"));
  // }, []);

  // 🟩 Fetch company info (logo)
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const company = data[0];
          if (company.logoPath) {
            setCompanyLogo(`${API_BASE_URL}/${company.logoPath}`);
          }
        }
      })
      .catch((err) => console.error("Company logo fetch error:", err));
  }, []);


  // ✅ Fetch profile data
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/users/admins`, {
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch admin info");
        return res.json();
      })
      .then((data) => {
        if (data && data.length > 0) {
          const admin = data[0];
          setProfile((prev) => ({
            ...prev,
            username: admin.name || prev.username,
            email: admin.email,
            role:
              sessionStorage.getItem("activeRole") ||
              (Array.isArray(admin.roles) ? admin.roles[0] : prev.role),

            roles: admin.roles || prev.roles, // VERY IMPORTANT
            status: admin.status,
          }));

        }
      })
      .catch((err) => {
        console.error("Admin fetch error:", err);
      });
  }, []);

  // ✅ Load bootstrap CSS dynamically
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);



  return (
    <>
      {/* 🔹 Top White Bar */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2 shadow-sm position-relative"
        style={{ backgroundColor: "#fff", borderBottom: "1px solid #ddd" }}
      >
        {/* Left: Logo */}
        <div className="d-flex align-items-center">
          <img src={logo} style={{ height: "40px", marginRight: "45px" }} />

          {/* Company Logo (from API) */}
          {companyLogo && (
            <img
              src={companyLogo}
              alt="Company Logo"
              style={{
                height: "40px",
                width: "auto",
                objectFit: "contain",
                borderRadius: "6px",
              }}
            />
          )}
        </div>



        {/* Right: Icons + Profile */}



        <div className="d-flex align-items-center gap-3">
          <a
            href="/admindashboard"
            className="text-decoration-none text-dark d-flex align-items-center"
          >
            <FaHome className="me-1" size={23} />
          </a>
          <a
            href="/settingpage"
            className="text-decoration-none text-dark fw-semibold d-flex align-items-center"
          >
            <FaSlidersH className="me-1" size={19} /> Settings
          </a>


          <div
            className="position-relative"
            onMouseEnter={() => setOpenDropdown("profile")}
            onMouseLeave={handleMouseLeave}
          >
            <span
              className="d-flex align-items-center text-dark"
              style={{ cursor: "pointer" }}
            >
              {/* Profile picture with loading / error handling */}
              <div style={{ width: 37, height: 37, position: "relative", marginRight: 8 }}>
                {/* spinner */}
                {photoLoading && (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                      width: 37,
                      height: 37,
                      borderRadius: "50%",
                      background: "#f0f0f0",
                    }}
                  >
                    <div className="spinner-border spinner-border-sm" role="status" />
                  </div>
                )}

                {/* actual photo */}
                {!photoLoading && profile.photo && !photoError && (
                  <img
                    src={profile.photo}
                    alt="Profile"
                    onError={() => {
                      setPhotoError(true);
                      setProfile((p) => ({ ...p, photo: null }));
                    }}
                    style={{
                      width: 37,
                      height: 37,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                )}

                {/* fallback */}
                {!photoLoading && (!profile.photo || photoError) && (
                  <div
                    style={{
                      width: 37,
                      height: 37,
                      borderRadius: "50%",
                      background: "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      color: "#374151",
                      fontSize: 14,
                    }}
                  >
                    {(profile.username || "U").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>


              <div className="d-flex flex-column fw-semibold">
                <span>{profile.username}</span>
                <small className="text-muted">
                  {(sessionStorage.getItem("activeRole") || profile.role)
                    ?.replace("ROLE_", "")}
                </small>


              </div>
            </span>

            {openDropdown === "profile" && (
              <div
                className="dropdown-menu show p-2"
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  zIndex: 2000,
                  minWidth: "220px",
                }}
              >
                <a className="dropdown-item" href="/adminprofile">
                  Profile Settings
                </a>
                <div className="dropdown-divider"></div>

                {profile.roles && profile.roles.length > 1 && (
                  <div className="px-2">
                    <h6 className="text-muted fw-bold mb-2">Switch Role</h6>
                    <table className="table table-sm mb-2">
                      <tbody>
                        {profile.roles
                          .filter((role) => role !== activeRole)
                          .map((role, idx) => {
                            let path = "/";
                            if (role === "ROLE_MANAGER")
                              path = "/managerdashboard";
                            if (role === "ROLE_ADMIN") path = "/admindashboard";
                            if (role === "ROLE_USER") path = "/empdashboard";

                            return (
                              <tr key={idx}>
                                <td>
                                  <a
                                    href={path}
                                    className="text-decoration-none d-block"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      sessionStorage.setItem("activeRole", role);
                                      setProfile((prev) => ({
                                        ...prev,
                                        role: role.replace("ROLE_", ""),
                                      }));
                                      navigate(path);
                                    }}
                                  >
                                    {role.replace("ROLE_", "")}
                                  </a>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="dropdown-divider"></div>
                <a
                  className="dropdown-item text-danger"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    sessionStorage.removeItem("token");
                    sessionStorage.removeItem("activeRole");
                    window.location.href = "/";
                  }}
                >
                  Logout
                </a>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* 🔹 Main Navbar */}
      <>
        <style>
          {`
      .navbar .dropdown-toggle::after {
        display: none !important;
      }
    `}
        </style>
        <Navbar
          expand="lg"
          variant="dark"
          className="shadow-sm"
          style={{
            backgroundColor: navbarColor,
            paddingTop: "0.05rem",
            paddingBottom: "0.05rem",
            minHeight: "40px",
          }}
        >
          <Container fluid>
            {/* <Navbar.Brand href="/admindashboard" className="fw-bold">
              <FaHome className="me-2" />
            </Navbar.Brand>
            <Navbar.Brand href="#" className="fw-bold">
              <fa-watch className="me-2" />
            </Navbar.Brand>
            <Navbar.Brand href="#" className="fw-bold">
              <FaStar className="me-2" />
            </Navbar.Brand> */}

            <Navbar.Toggle aria-controls="admin-navbar-nav" />
            <Navbar.Collapse id="admin-navbar-nav">
              {/* Left Tabs */}
              <Nav className=" me-auto fw-semibold">
                <NavDropdown
                  title={<span
                    style={{ color: "#ffffff", fontWeight: "600", cursor: "pointer" }}
                    onClick={() => navigate("/masterslanding")}
                  >
                    Masters
                  </span>}
                  id="masters-dropdown"
                  show={openDropdown === "masters"}
                  onMouseEnter={() => handleMouseEnter("masters")}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavDropdown.Item href="/department">
                    Departments
                  </NavDropdown.Item>
                  <NavDropdown.Item href="/role">Roles</NavDropdown.Item>
                  <NavDropdown.Item href="/designation">Designations</NavDropdown.Item>
                  <NavDropdown.Item href="/document">Document</NavDropdown.Item>
                  <NavDropdown.Item href="/leavetype">
                    LeaveTypes
                  </NavDropdown.Item>
                  <NavDropdown.Item href="/team">
                    Team
                  </NavDropdown.Item>
                  <NavDropdown.Item href="/project">
                    Project
                  </NavDropdown.Item>
                  <NavDropdown.Item href="/holiday">
                    Holidays
                  </NavDropdown.Item>
                </NavDropdown>

                <NavDropdown
                  title={<span style={{ color: "#ffffff", fontWeight: "600" }} onClick={() => navigate("/employeelanding")}>Employee</span>}
                  id="employees-dropdown"
                  show={openDropdown === "employees"}
                  onMouseEnter={() => handleMouseEnter("employees")}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavDropdown.Item href="/addemployee">New</NavDropdown.Item>
                  <NavDropdown.Item href="/allemployee">List</NavDropdown.Item>
                  <NavDropdown.Item href="/viewalldocs">Documents</NavDropdown.Item>
                  {/* <NavDropdown.Item href="/leavelist">Leave Management</NavDropdown.Item> */}
                  <NavDropdown.Item href="adminmanageteams">Team Management</NavDropdown.Item>
                </NavDropdown>



                {/* <NavDropdown
                  title={<span style={{ color: "#ffffff", fontWeight: "600" }}>Users</span>}
                  id="users-dropdown"
                  show={openDropdown === "users"}
                  onMouseEnter={() => handleMouseEnter("users")}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavDropdown.Item href="/users/list">
                    User List
                  </NavDropdown.Item>
                  <NavDropdown.Item href="/users/add">Add User</NavDropdown.Item>
                </NavDropdown> */}

                {/* <NavDropdown
                  title={<span style={{ color: "#ffffff", fontWeight: "600" }}>User Documents</span>}
                  id="userdocs-dropdown"
                  show={openDropdown === "userdocs"}
                  onMouseEnter={() => handleMouseEnter("userdocs")}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavDropdown.Item href="/documents/upload">
                    Upload Documents
                  </NavDropdown.Item>
                  <NavDropdown.Item href="/documents/manage">
                    Manage Documents
                  </NavDropdown.Item>
                </NavDropdown> */}

                <NavDropdown
                  title={<span style={{ color: "#ffffff", fontWeight: "600" }} onClick={() => navigate("/admintasklanding")}>Tasks</span>}
                  id="tasks-dropdown"
                  show={openDropdown === "tasks"}
                  onMouseEnter={() => handleMouseEnter("tasks")}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavDropdown.Item href="/adminviewtasks">Review Task</NavDropdown.Item>
                  {/* <NavDropdown.Item href="/tasks/assign">
                    Abc
                  </NavDropdown.Item> */}
                </NavDropdown>

                <NavDropdown
                  title={<span style={{ color: "#ffffff", fontWeight: "600" }} onClick={() => navigate("/admintimesheetlanding")}>Timesheets</span>}
                  id="timesheets-dropdown"
                  show={openDropdown === "timesheets"}
                  onMouseEnter={() => handleMouseEnter("timesheets")}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavDropdown.Item href="/admintimesheetgrid">
                    View Timesheets
                  </NavDropdown.Item>
                  {/* <NavDropdown.Item href="#">
                    Abc
                  </NavDropdown.Item> */}
                </NavDropdown>

                <Nav.Link
                  className="px-4"
                  style={{
                    color: "#ffffff",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/adminleavemanagementlanding")}
                >
                  Leave Management
                </Nav.Link>


              </Nav>

              {/* Right Side - Icons */}
              <Nav className="ms-auto align-items-center">
                {/* <Nav.Link href="/support">
                <FaHeadset className="me-1" /> Support
              </Nav.Link>
              <Nav.Link href="/help">
                <FaQuestionCircle className="me-1" /> Help
              </Nav.Link>
              <Nav.Link href="/feedback">
                <FaCommentDots className="me-1" /> Feedback
              </Nav.Link>
              <Nav.Link href="/profile">
                <FaUserCircle className="me-1" /> Profile
              </Nav.Link> */}
              </Nav>
              {/* 🔹 Search Bar on the exact right */}
              <div className="ms-auto d-flex align-items-center" style={{ maxWidth: "250px", width: "100%" }}>
                <div className="d-flex align-items-center border rounded px-2 w-100" style={{ background: "#fff" }}>
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control border-0"
                    style={{ boxShadow: "none" }}
                  />
                  <FaSearch style={{ color: "#666" }} />
                </div>
              </div>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </>
    </>
  );
};

export default AdminNavbar;
